import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useState, useCallback, useMemo } from 'react';

export interface SearchFilters {
  category?: string;
  occasion?: string;
  priceRange?: {
    min: number;
    max: number;
  };
  inStock?: boolean;
  sortBy?: 'name' | 'price' | 'popularity' | 'newest';
  sortOrder?: 'asc' | 'desc';
}

export interface SearchResult {
  id: string;
  name_en: string;
  name_ar: string;
  description_en: string;
  description_ar: string;
  price: number;
  original_price?: number;
  images: string[];
  category: {
    id: string;
    name_en: string;
    name_ar: string;
    slug: string;
  };
  occasion?: {
    id: string;
    name_en: string;
    name_ar: string;
    slug: string;
  };
  in_stock: boolean;
  rating?: number;
  review_count?: number;
  created_at: string;
}

export interface SearchSuggestion {
  id: string;
  name_en: string;
  name_ar: string;
  type: 'product' | 'category' | 'occasion';
  slug?: string;
}

const searchApi = {
  // Search products with filters
  async searchProducts(
    query: string,
    filters: SearchFilters = {},
    language: 'en' | 'ar' = 'en'
  ): Promise<SearchResult[]> {
    let supabaseQuery = supabase
      .from('products')
      .select(`
        id,
        name_en,
        name_ar,
        description_en,
        description_ar,
        price,
        original_price,
        images,
        stock_quantity,
        rating,
        review_count,
        created_at,
        categories!inner (
          id,
          name_en,
          name_ar,
          slug
        ),
        occasions (
          id,
          name_en,
          name_ar,
          slug
        )
      `)
      .eq('is_available', true);

    // Apply text search
    if (query.trim()) {
      const searchTerm = query.trim();
      supabaseQuery = supabaseQuery.or(
        `name_en.ilike.%${searchTerm}%,name_ar.ilike.%${searchTerm}%,description_en.ilike.%${searchTerm}%,description_ar.ilike.%${searchTerm}%`
      );
    }

    // Apply category filter
    if (filters.category) {
      supabaseQuery = supabaseQuery.eq('categories.slug', filters.category);
    }

    // Apply occasion filter
    if (filters.occasion) {
      supabaseQuery = supabaseQuery.eq('occasions.slug', filters.occasion);
    }

    // Apply price range filter
    if (filters.priceRange) {
      const { min, max } = filters.priceRange;
      if (min !== undefined) {
        supabaseQuery = supabaseQuery.gte('price', min);
      }
      if (max !== undefined) {
        supabaseQuery = supabaseQuery.lte('price', max);
      }
    }

    // Apply stock filter
    if (filters.inStock !== undefined) {
      supabaseQuery = supabaseQuery.gt('stock_quantity', 0);
    }

    // Apply sorting
    if (filters.sortBy) {
      let sortColumn = filters.sortBy;
      if (filters.sortBy === 'name') {
        sortColumn = (language === 'ar' ? 'name_ar' : 'name_en') as any;
      }
      supabaseQuery = supabaseQuery.order(sortColumn, {
        ascending: filters.sortOrder === 'asc'
      });
    } else {
      // Default sorting by relevance (if search query) or newest
      if (query.trim()) {
        // For search results, sort by name for better relevance
        const sortColumn = (language === 'ar' ? 'name_ar' : 'name_en') as any;
        supabaseQuery = supabaseQuery.order(sortColumn, { ascending: true });
      } else {
        supabaseQuery = supabaseQuery.order('created_at', { ascending: false });
      }
    }

    const { data, error } = await supabaseQuery;

    if (error) {
      console.error('Search error:', error);
      throw error;
    }

    // Transform the data to match SearchResult interface
    return (data || []).map(item => ({
      id: item.id,
      name_en: item.name_en,
      name_ar: item.name_ar,
      description_en: item.description_en || '',
      description_ar: item.description_ar || '',
      price: item.price,
      original_price: item.original_price,
      images: Array.isArray(item.images) ? item.images.map(img => String(img)) : [],
      category: item.categories,
      occasion: item.occasions,
      in_stock: (item.stock_quantity || 0) > 0,
      rating: item.rating,
      review_count: item.review_count,
      created_at: item.created_at
    }));
  },

  // Get search suggestions
  async getSearchSuggestions(
    query: string,
    language: 'en' | 'ar' = 'en'
  ): Promise<SearchSuggestion[]> {
    if (!query.trim() || query.length < 2) {
      return [];
    }

    const searchTerm = query.trim();
    const suggestions: SearchSuggestion[] = [];

    try {
      // Search products
      const { data: products } = await supabase
        .from('products')
        .select('id, name_en, name_ar')
        .eq('is_available', true)
        .or(`name_en.ilike.%${searchTerm}%,name_ar.ilike.%${searchTerm}%`)
        .limit(5);

      if (products) {
        suggestions.push(
          ...products.map(product => ({
            id: product.id,
            name_en: product.name_en,
            name_ar: product.name_ar,
            type: 'product' as const
          }))
        );
      }

      // Search categories
      const { data: categories } = await supabase
        .from('categories')
        .select('id, name_en, name_ar, slug')
        .eq('is_active', true)
        .or(`name_en.ilike.%${searchTerm}%,name_ar.ilike.%${searchTerm}%`)
        .limit(3);

      if (categories) {
        suggestions.push(
          ...categories.map(category => ({
            id: category.id,
            name_en: category.name_en,
            name_ar: category.name_ar,
            type: 'category' as const,
            slug: category.slug
          }))
        );
      }

      // Search occasions
      const { data: occasions } = await supabase
        .from('occasions')
        .select('id, name_en, name_ar, slug')
        .eq('is_active', true)
        .or(`name_en.ilike.%${searchTerm}%,name_ar.ilike.%${searchTerm}%`)
        .limit(3);

      if (occasions) {
        suggestions.push(
          ...occasions.map(occasion => ({
            id: occasion.id,
            name_en: occasion.name_en,
            name_ar: occasion.name_ar,
            type: 'occasion' as const,
            slug: occasion.slug
          }))
        );
      }

      // Sort suggestions by relevance (exact matches first)
      return suggestions.sort((a, b) => {
        const aName = language === 'ar' ? a.name_ar : a.name_en;
        const bName = language === 'ar' ? b.name_ar : b.name_en;
        
        const aExact = aName.toLowerCase().startsWith(searchTerm.toLowerCase());
        const bExact = bName.toLowerCase().startsWith(searchTerm.toLowerCase());
        
        if (aExact && !bExact) return -1;
        if (!aExact && bExact) return 1;
        
        return aName.localeCompare(bName);
      });
    } catch (error) {
      console.error('Search suggestions error:', error);
      return [];
    }
  },

  // Get popular searches
  async getPopularSearches(language: 'en' | 'ar' = 'en'): Promise<string[]> {
    const popularSearches = {
      en: [
        'roses',
        'birthday flowers',
        'wedding flowers',
        'anniversary gifts',
        'get well flowers',
        'sympathy flowers',
        'tulips',
        'lilies',
        'orchids',
        'sunflowers'
      ],
      ar: [
        'ورود',
        'ورد عيد الميلاد',
        'ورد الزفاف',
        'هدايا الذكرى السنوية',
        'ورد الشفاء',
        'ورد التعزية',
        'توليب',
        'زنابق',
        'أوركيد',
        'دوار الشمس'
      ]
    };

    return popularSearches[language] || popularSearches.en;
  },

  // Get search filters (categories, occasions, price ranges)
  async getSearchFilters() {
    try {
      // Get categories
      const { data: categories } = await supabase
        .from('categories')
        .select('id, name_en, name_ar, slug')
        .eq('is_active', true)
        .order('name_en');

      // Get occasions
      const { data: occasions } = await supabase
        .from('occasions')
        .select('id, name_en, name_ar, slug')
        .eq('is_active', true)
        .order('name_en');

      // Get price ranges
      const { data: priceStats } = await supabase
        .from('products')
        .select('price')
        .eq('is_available', true);

      const prices = priceStats?.map(p => p.price) || [];
      const minPrice = Math.min(...prices);
      const maxPrice = Math.max(...prices);

      return {
        categories: categories || [],
        occasions: occasions || [],
        priceRange: {
          min: Math.floor(minPrice / 100) * 100, // Round down to nearest 100
          max: Math.ceil(maxPrice / 100) * 100   // Round up to nearest 100
        }
      };
    } catch (error) {
      console.error('Get search filters error:', error);
      return {
        categories: [],
        occasions: [],
        priceRange: { min: 0, max: 1000 }
      };
    }
  }
};

// React Query Hooks
export const useSearch = (
  query: string,
  filters: SearchFilters = {},
  language: 'en' | 'ar' = 'en'
) => {
  return useQuery({
    queryKey: ['search', query, filters, language],
    queryFn: () => searchApi.searchProducts(query, filters, language),
    enabled: true,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

export const useSearchSuggestions = (
  query: string,
  language: 'en' | 'ar' = 'en'
) => {
  return useQuery({
    queryKey: ['search-suggestions', query, language],
    queryFn: () => searchApi.getSearchSuggestions(query, language),
    enabled: query.length >= 2,
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
};

export const usePopularSearches = (language: 'en' | 'ar' = 'en') => {
  return useQuery({
    queryKey: ['popular-searches', language],
    queryFn: () => searchApi.getPopularSearches(language),
    staleTime: 30 * 60 * 1000, // 30 minutes
  });
};

export const useSearchFilters = () => {
  return useQuery({
    queryKey: ['search-filters'],
    queryFn: () => searchApi.getSearchFilters(),
    staleTime: 10 * 60 * 1000, // 10 minutes
  });
};

// Custom hook for search state management
export const useSearchState = (initialQuery: string = '') => {
  const [query, setQuery] = useState(initialQuery);
  const [filters, setFilters] = useState<SearchFilters>({});
  const [language, setLanguage] = useState<'en' | 'ar'>('en');

  const updateQuery = useCallback((newQuery: string) => {
    setQuery(newQuery);
  }, []);

  const updateFilters = useCallback((newFilters: Partial<SearchFilters>) => {
    setFilters(prev => ({ ...prev, ...newFilters }));
  }, []);

  const clearFilters = useCallback(() => {
    setFilters({});
  }, []);

  const resetSearch = useCallback(() => {
    setQuery('');
    setFilters({});
  }, []);

  const searchResults = useSearch(query, filters, language);
  const suggestions = useSearchSuggestions(query, language);
  const popularSearches = usePopularSearches(language);
  const searchFilters = useSearchFilters();

  return {
    // State
    query,
    filters,
    language,
    
    // Actions
    updateQuery,
    updateFilters,
    clearFilters,
    resetSearch,
    setLanguage,
    
    // Results
    searchResults,
    suggestions,
    popularSearches,
    searchFilters,
    
    // Computed
    hasActiveFilters: Object.keys(filters).length > 0,
    isSearching: searchResults.isLoading,
    totalResults: searchResults.data?.length || 0
  };
};

// Utility functions
export const formatSearchQuery = (query: string): string => {
  return query.trim().toLowerCase();
};

export const highlightSearchTerm = (
  text: string,
  searchTerm: string,
  language: 'en' | 'ar' = 'en'
): string => {
  if (!searchTerm.trim()) return text;
  
  const regex = new RegExp(`(${searchTerm})`, 'gi');
  return text.replace(regex, '<mark>$1</mark>');
};

export const getSearchResultUrl = (result: SearchSuggestion): string => {
  switch (result.type) {
    case 'product':
      return `/product/${result.id}`;
    case 'category':
      return `/category/${result.slug}`;
    case 'occasion':
      return `/occasion/${result.slug}`;
    default:
      return '/';
  }
}; 